/*
 * A Home To do item as a list you fix from (owner, 2026-11-02: "help me actually fix these problems and not just tell me
 * and bring me to an area with no way to see why and fix it"). Each row is one real visit, client, invoice, quote or
 * request (api home/todo.ts), says why it is on the list, and has the fix right there: Assign crew, Mark done or Skip,
 * Book or Not active, Charge the card or Send again, Call or Text. A fixed row leaves the list.
 *
 * Clients with nothing booked can be ticked and set not active together (owner, Nov 2: 204 of 206 came over with no visit
 * or invoice). Over 20 at once asks the owner to confirm (clients/bulk), and Undo sets them active again.
 */
import { Link, useParams } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";
import { INACTIVE_REASONS, TODO_KINDS, type TodoItem, type TodoKind } from "@steward/shared";
import { ApiError, api } from "../api/client";
import { useBulkClients, useCompleteVisit, useDeactivateClient, usePeople, useVisitCrew, type BulkInput } from "../api/hooks";
import { useChargeCard, useRequests, useSendInvoice, useSendQuote } from "../api/money";
import { useSession } from "../app/session";
import { money, plural, shortDate } from "../components/format";
import { Icon } from "../components/Icon";
import { Menu, useToast } from "../components/overlays";
import { useCancelSheet } from "../components/CancelSheet";
import { PlanOfferSend } from "../components/PlanOffer";
import { Button, Card, EmptyState, LoadError, Page, Skeleton } from "../components/parts";
import { useQuery, useQueryClient } from "@tanstack/react-query";

const TITLES: Record<TodoKind | "new_requests", { title: string; about: string }> = {
  late_visits: {
    title: "Visits not marked done",
    about: "From the last 7 days. If the work was done, mark it done so it gets billed. If not, skip it.",
  },
  no_crew: { title: "Visits with no crew", about: "In the next 7 days. Pick who goes." },
  idle_clients: {
    title: "Clients with nothing booked",
    about: "Active clients with no job. Newest last visit first. Book them, reach out, or set them not active.",
  },
  past_due: { title: "Invoices past due", about: "Most late first. Charge the card on file or send it again." },
  awaiting: { title: "Invoices waiting for payment", about: "Not due yet. Charge the card on file or send it again." },
  quotes_waiting: { title: "Quotes waiting on the client", about: "Sent, no answer yet. Send it again or give them a call." },
  to_check: { title: "Invoices to check", about: "Look each one over and send it." },
  plan_offers: {
    title: "Plan offers to send",
    about:
      "Asked for a regular cleaning, or cleaned yesterday or today and not on a plan. Send each one an offer with their prices.",
  },
  new_requests: { title: "New requests", about: "Newest first. Open one to check the price and send the quote." },
};

const REASON_WORDS: Record<(typeof INACTIVE_REASONS)[number], string> = {
  unpaid: "Did not pay",
  complained: "Complained",
  cancelled: "Cancelled",
  moved: "Moved away",
  elsewhere: "Went with someone else",
  not_needed: "Does not need us now",
  other: "Other",
};

const errText = (e: unknown) => (e instanceof Error ? e.message : "That did not work. Try again.");

export function TodoFix() {
  const { kind } = useParams({ strict: false });
  const k = kind ?? "";
  if (k === "new_requests") return <NewRequests />;
  if (!(TODO_KINDS as readonly string[]).includes(k)) return <LoadError error={new Error("That list was not found.")} />;
  return <FixList kind={k as TodoKind} />;
}

function Frame({ kind, count, children }: { kind: TodoKind | "new_requests"; count: number | null; children: ReactNode }) {
  const t = TITLES[kind];
  return (
    <Page title={count === null ? t.title : `${t.title} (${String(count)})`} subtitle={t.about}>
      {children}
    </Page>
  );
}

function FixList({ kind }: { kind: TodoKind }) {
  const { company } = useSession();
  const q = useQuery({
    queryKey: ["todo", company.id, kind],
    queryFn: () => api<{ items: TodoItem[] }>("GET", `home/todo/${kind}`),
  });
  // Rows fixed here leave the list at once; the next load agrees.
  const [gone, setGone] = useState<Set<string>>(new Set());
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const { can } = useSession();
  const items = (q.data?.items ?? []).filter((i) => !gone.has(i.id));
  const done = (id: string) => setGone((s) => new Set([...s, id]));
  const many = kind === "idle_clients" && can("client.bulk");
  const tick = (id: string, on: boolean) =>
    setTicked((s) => {
      const n = new Set(s);
      if (on) n.add(id);
      else n.delete(id);
      return n;
    });
  return (
    <Frame kind={kind} count={q.data ? items.length : null}>
      {many && items.length ? (
        <ManyNotActive
          items={items}
          ticked={ticked}
          setTicked={setTicked}
          onGone={(ids) => setGone((s) => new Set([...s, ...ids]))}
          onBack={(ids) => setGone((s) => new Set([...s].filter((x) => !ids.includes(x))))}
        />
      ) : null}
      {q.error ? (
        <LoadError error={q.error} retry={() => void q.refetch()} />
      ) : !q.data ? (
        <Skeleton rows={6} />
      ) : items.length === 0 ? (
        <Card>
          <EmptyState icon="check" title="All done">
            Nothing left on this list.
          </EmptyState>
        </Card>
      ) : (
        <Card flush>
          <div className="todo fix-list">
            {items.map((i) => (
              <FixRow
                key={i.id}
                kind={kind}
                item={i}
                onDone={() => done(i.id)}
                {...(many ? { ticked: ticked.has(i.id), onTick: (on: boolean) => tick(i.id, on) } : {})}
              />
            ))}
          </div>
        </Card>
      )}
    </Frame>
  );
}

function FixRow({
  kind,
  item,
  onDone,
  ticked,
  onTick,
}: {
  kind: TodoKind;
  item: TodoItem;
  onDone: () => void;
  ticked?: boolean;
  onTick?: (on: boolean) => void;
}) {
  const late = kind === "past_due" || kind === "late_visits";
  return (
    <div className={`item fix-row${ticked ? " ticked" : ""}`}>
      {onTick ? (
        <input
          type="checkbox"
          className="fix-tick"
          checked={Boolean(ticked)}
          onChange={(e) => onTick(e.target.checked)}
          aria-label={`Tick ${item.title}`}
        />
      ) : null}
      <div className="fix-main">
        <OpenLink kind={kind} item={item} />
        <span className="muted small">{item.sub}</span>
        <span className={`small${late ? " fix-why late" : " fix-why"}`}>{item.why}</span>
      </div>
      {item.amountCents !== null ? <span className="num fix-amount">{money(item.amountCents)}</span> : null}
      <div className="fix-actions">
        <Reach item={item} />
        <Fixes kind={kind} item={item} onDone={onDone} />
      </div>
    </div>
  );
}

function OpenLink({ kind, item }: { kind: TodoKind; item: TodoItem }) {
  if (kind === "late_visits" || kind === "no_crew")
    return (
      <Link to="/visits/$ref" params={{ ref: item.id }} className="fix-title">
        {item.title}
      </Link>
    );
  if (kind === "idle_clients")
    return (
      <Link to="/clients/$id" params={{ id: item.id }} className="fix-title">
        {item.title}
      </Link>
    );
  if (kind === "plan_offers")
    return (
      <Link to="/clients/$id" params={{ id: item.id }} className="fix-title">
        {item.title}
      </Link>
    );
  if (kind === "quotes_waiting")
    return (
      <Link to="/quotes/$id" params={{ id: item.id }} className="fix-title">
        {item.title}
      </Link>
    );
  return (
    <Link to="/invoices/$id" params={{ id: item.id }} className="fix-title">
      {item.title}
    </Link>
  );
}

/** Call and text the client, right from the row. */
function Reach({ item }: { item: TodoItem }) {
  if (!item.phone) return null;
  return (
    <>
      <a className="btn tertiary small" href={`tel:${item.phone}`} aria-label={`Call ${item.title}`}>
        <Icon name="phone" size={14} /> Call
      </a>
      <a className="btn tertiary small" href={`sms:${item.phone}`} aria-label={`Text ${item.title}`}>
        <Icon name="message" size={14} /> Text
      </a>
    </>
  );
}

function Fixes({ kind, item, onDone }: { kind: TodoKind; item: TodoItem; onDone: () => void }) {
  const { company, can } = useSession();
  const toast = useToast();
  const qc = useQueryClient();
  const people = usePeople(company.id, kind === "no_crew" && can("visit.update"));
  const crew = useVisitCrew();
  const complete = useCompleteVisit();
  const why = useCancelSheet();
  const deactivate = useDeactivateClient();
  const charge = useChargeCard();
  const sendInvoice = useSendInvoice();
  const sendQuote = useSendQuote();
  const [offering, setOffering] = useState(false);
  const ok = (words: string) => () => {
    toast(words);
    onDone();
    void qc.invalidateQueries({ queryKey: ["home"] });
  };
  const fail = (e: unknown) => toast(errText(e), { kind: "error" });
  // Send again the way they like, when we can reach them that way.
  const via = item.prefers === "email" && item.email ? "email" : item.phone ? "text" : item.email ? "email" : null;
  const to = via === "email" ? item.email : item.phone;

  if (kind === "no_crew") {
    if (!can("visit.update")) return null;
    const team = (people.data ?? []).filter((p) => p.active);
    return (
      <Menu
        button={({ toggle }) => (
          <Button size="small" icon="team" loading={crew.isPending} onClick={toggle}>
            Assign crew
          </Button>
        )}
      >
        {(close) =>
          team.length ? (
            team.map((p) => (
              <button
                key={p.id}
                type="button"
                role="menuitem"
                onClick={() => {
                  close();
                  crew.mutate(
                    { ref: item.id, crew: [p.id], scope: "this" },
                    { onSuccess: ok(`${p.name} is on it`), onError: fail },
                  );
                }}
              >
                {p.name}
              </button>
            ))
          ) : (
            <span className="muted small">No team yet</span>
          )
        }
      </Menu>
    );
  }
  if (kind === "late_visits") {
    if (!can("visit.update")) return null;
    return (
      <>
        <Button
          size="small"
          type="secondary"
          onClick={() =>
            why.off({ ref: item.id, date: "", startTime: null, clientName: item.title, past: true }, "skipped", () => {
              onDone();
              void qc.invalidateQueries({ queryKey: ["home"] });
            })
          }
        >
          Skip
        </Button>
        {why.sheet}
        <Button
          size="small"
          icon="check"
          loading={complete.isPending}
          onClick={() => complete.mutate({ ref: item.id }, { onSuccess: ok("Marked done"), onError: fail })}
        >
          Mark done
        </Button>
      </>
    );
  }
  if (kind === "idle_clients")
    return (
      <>
        {can("client.update") ? (
          <Menu
            button={({ toggle }) => (
              <Button size="small" type="secondary" loading={deactivate.isPending} onClick={toggle}>
                Not active
              </Button>
            )}
          >
            {(close) =>
              INACTIVE_REASONS.map((r) => (
                <button
                  key={r}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    close();
                    deactivate.mutate(
                      { id: item.id, body: { reason: r } },
                      { onSuccess: ok(`${item.title} set not active`), onError: fail },
                    );
                  }}
                >
                  {REASON_WORDS[r]}
                </button>
              ))
            }
          </Menu>
        ) : null}
        {can("job.update") ? (
          <Link to="/jobs/new" search={{ clientId: item.id }} className="btn small">
            Book
          </Link>
        ) : null}
      </>
    );
  if (kind === "past_due" || kind === "awaiting")
    return (
      <>
        {via && to && can("invoice.send") ? (
          <Button
            size="small"
            type="secondary"
            loading={sendInvoice.isPending}
            onClick={() =>
              sendInvoice.mutate(
                { id: item.id, via, to },
                {
                  onSuccess: () => {
                    toast(`Sent again by ${via === "text" ? "text" : "email"}`);
                    void qc.invalidateQueries({ queryKey: ["todo"] });
                  },
                  onError: fail,
                },
              )
            }
          >
            Send again
          </Button>
        ) : null}
        {item.cardLast4 && item.amountCents && can("card.charge") ? (
          <Button
            size="small"
            loading={charge.isPending}
            onClick={() => {
              if (!window.confirm(`Charge ${money(item.amountCents!)} to the card ending ${item.cardLast4!}?`)) return;
              charge.mutate(
                { id: item.id, key: crypto.randomUUID(), amountCents: item.amountCents!, last4: item.cardLast4! },
                { onSuccess: ok("Charged. A receipt goes to the client."), onError: fail },
              );
            }}
          >
            Charge card {item.cardLast4}
          </Button>
        ) : null}
      </>
    );
  if (kind === "plan_offers")
    return can("quote.update") ? (
      <>
        <Button size="small" type="secondary" onClick={() => setOffering(true)}>
          Send offer
        </Button>
        {offering ? (
          <PlanOfferSend
            clientId={item.id}
            opener={item.offer?.opener}
            leadId={item.offer?.leadId ?? null}
            onClose={() => setOffering(false)}
            onSent={() => {
              onDone();
              void qc.invalidateQueries({ queryKey: ["home"] });
            }}
          />
        ) : null}
      </>
    ) : null;
  if (kind === "quotes_waiting")
    return via && to && can("quote.update") ? (
      <Button
        size="small"
        type="secondary"
        loading={sendQuote.isPending}
        onClick={() =>
          sendQuote.mutate(
            { id: item.id, via, to },
            { onSuccess: () => toast(`Sent again by ${via === "text" ? "text" : "email"}`), onError: fail },
          )
        }
      >
        Send again
      </Button>
    ) : null;
  // Invoices to check: open each one to look it over.
  return (
    <Link to="/invoices/$id" params={{ id: item.id }} className="btn small">
      Check and send
    </Link>
  );
}

/** New requests from the list Home already has, newest first, with what they asked for and the price if there is one. */
function NewRequests() {
  const { company } = useSession();
  const q = useRequests(company.id);
  const rows = (q.data?.requests ?? [])
    .filter((r) => r.state === "new" && !r.archived)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  return (
    <Frame kind="new_requests" count={q.data ? rows.length : null}>
      {q.error ? (
        <LoadError error={q.error} retry={() => void q.refetch()} />
      ) : !q.data ? (
        <Skeleton rows={6} />
      ) : rows.length === 0 ? (
        <Card>
          <EmptyState icon="check" title="All done">
            No new requests.
          </EmptyState>
        </Card>
      ) : (
        <Card flush>
          <div className="todo fix-list">
            {rows.map((r) => (
              <div key={r.id} className="item fix-row">
                <div className="fix-main">
                  <Link to="/requests/$id" params={{ id: r.id }} className="fix-title">
                    {r.name}
                  </Link>
                  <span className="muted small">{[r.service, r.address].filter(Boolean).join(", ") || "No details yet"}</span>
                  <span className="small fix-why">
                    Came in {shortDate(r.createdAt.slice(0, 10))}
                    {r.source ? ` from ${r.source.replace(/_/g, " ")}` : ""}.{" "}
                    {r.quote ? `Quote #${String(r.quote.number)} is a draft, check it and send it.` : "No quote yet."}
                  </span>
                </div>
                {r.quote ? <span className="num fix-amount">{money(r.quote.totalCents)}</span> : null}
                <div className="fix-actions">
                  {r.phone ? (
                    <a className="btn tertiary small" href={`tel:${r.phone}`}>
                      <Icon name="phone" size={14} /> Call
                    </a>
                  ) : null}
                  {r.quote ? (
                    <Link to="/quotes/$id" params={{ id: r.quote.id }} className="btn small">
                      Check the quote
                    </Link>
                  ) : (
                    <Link to="/requests/$id" params={{ id: r.id }} className="btn small">
                      Open
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
      <p className="muted small">{plural(rows.length, "request")} waiting for a quote.</p>
    </Frame>
  );
}

/** Tick clients with nothing booked and set them not active together, with one reason. */
function ManyNotActive({
  items,
  ticked,
  setTicked,
  onGone,
  onBack,
}: {
  items: TodoItem[];
  ticked: Set<string>;
  setTicked: (s: Set<string>) => void;
  onGone: (ids: string[]) => void;
  onBack: (ids: string[]) => void;
}) {
  const bulk = useBulkClients();
  const toast = useToast();
  const qc = useQueryClient();
  const [reason, setReason] = useState<(typeof INACTIVE_REASONS)[number]>("not_needed");
  const never = items.filter((i) => i.days === null).map((i) => i.id);
  const ids = items.filter((i) => ticked.has(i.id)).map((i) => i.id);
  // Over 20 the server asks the owner to confirm with a token; the same flow as the Clients page.
  const run = (input: BulkInput, undo: boolean) =>
    bulk.mutate(input, {
      onSuccess: (a) => {
        void qc.invalidateQueries({ queryKey: ["home"] });
        if (undo) {
          onBack(input.ids);
          toast(`${plural(a.done, "client")} active again`);
          return;
        }
        onGone(input.ids);
        setTicked(new Set());
        toast(`${plural(a.done, "client")} set not active`, {
          undo: () => run({ ids: input.ids, action: "reactivate" }, true),
        });
      },
      onError: (e) => {
        if (e instanceof ApiError && e.code === "confirm_needed" && typeof e.extra.confirm === "string") {
          if (window.confirm(e.message)) run({ ...input, confirm: e.extra.confirm }, undo);
        } else toast(errText(e), { kind: "error" });
      },
    });
  return (
    <Card>
      <div className="row tight fix-many">
        <Button size="small" type="secondary" disabled={!never.length} onClick={() => setTicked(new Set(never))}>
          Tick the {plural(never.length, "client")} with no visit or invoice
        </Button>
        <Button size="small" type="tertiary" onClick={() => setTicked(new Set(items.map((i) => i.id)))}>
          Tick all
        </Button>
        {ticked.size ? (
          <Button size="small" type="tertiary" onClick={() => setTicked(new Set())}>
            Clear
          </Button>
        ) : null}
      </div>
      {ids.length ? (
        <div className="row tight fix-many">
          <label className="small">
            Why{" "}
            <select value={reason} onChange={(e) => setReason(e.target.value as (typeof INACTIVE_REASONS)[number])}>
              {INACTIVE_REASONS.map((r) => (
                <option key={r} value={r}>
                  {REASON_WORDS[r]}
                </option>
              ))}
            </select>
          </label>
          <Button
            size="small"
            variation="destructive"
            loading={bulk.isPending}
            onClick={() => run({ ids, action: "deactivate", reason }, false)}
          >
            Set {plural(ids.length, "client")} not active
          </Button>
        </div>
      ) : (
        <p className="muted small">Tick clients to set them not active together. You can undo it.</p>
      )}
    </Card>
  );
}
